import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { Spinner } from "react-bootstrap";
import CommonToaster from "../../Common/CommonToaster";
import useAssignmentSession, { editorOptions } from "./useAssignmentSession";
import { CheckCircle2, XCircle, Play, Terminal, UploadCloud, ChevronDown, ChevronUp, Lightbulb, ListOrdered, Keyboard, Code, Clock, AlertTriangle } from "lucide-react";
import "../../Practice/practice.scss";
import "./assignmentStyles.scss";

const TABS = [
  { key: "input", label: "Input", icon: <Keyboard size="1em" /> },
  { key: "output", label: "Output", icon: <Terminal size="1em" /> },
  { key: "results", label: "Test Results", icon: <ListOrdered size="1em" /> },
];

const StudentAssignmentSessionPage = () => {
  const { id: sessionId } = useParams();
  const {
    session,
    loading,
    currentIdx,
    submittedQuestions,
    languages,
    input,
    setInput,
    output,
    results,
    running,
    activeTab,
    setActiveTab,
    bottomOpen,
    setBottomOpen,
    timeLeft,
    tabSwitches,
    toast,
    setToast,
    currentQ,
    currentLang,
    currentCode,
    isCurrentSubmitted,
    testCases,
    passedCount,
    visibleResults,
    hiddenResults,
    hiddenPassed,
    lockedLanguages,
    recommendedLangs,
    otherLangs,
    timerDanger,
    timerWarning,
    handleLangChange,
    handleCodeChange,
    handleRun,
    handleRunAll,
    handleSubmit,
    handleQuestionSwitch,
    onEditorMount,
  } = useAssignmentSession(sessionId);
  const [leftWidth, setLeftWidth] = useState(38);

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  /* ════════════════════════════════════════════
     Resizable divider
     ════════════════════════════════════════════ */

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const container = e.target.closest(".hr-split-container");
    if (!container) return;
    const totalW = container.getBoundingClientRect().width;
    const onMove = (ev) => {
      const pct = startW + ((ev.clientX - startX) / totalW) * 100;
      setLeftWidth(Math.min(60, Math.max(22, pct)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [leftWidth]);

  /* ════════════════════════════════════════════
     Render
     ════════════════════════════════════════════ */

  if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  if (!session) return <div className="as-error-state">Could not load assignment session.</div>;
  if (session.status === "submitted") return <div className="as-done-state">Assignment has been submitted.</div>;
  if (session.status === "expired") return <div className="as-done-state as-expired">Assignment has expired.</div>;

  return (
    <div className="hr-practice-page as-session-page user-select-none">
      {/* ── Top Bar ── */}
      <div className="hr-topbar as-topbar">
        <span className="hr-topbar-title">{session.assignment?.title}</span>

        {/* Question navigation */}
        <div className="as-question-nav">
          {session.questions.map((q, idx) => (
            <button
              key={q._id}
              className={`as-qnav-btn ${idx === currentIdx ? "active" : ""} ${submittedQuestions[q._id] ? "submitted" : ""}`}
              onClick={() => handleQuestionSwitch(idx)}
            >
              {submittedQuestions[q._id] && <CheckCircle2 size={9} />}
              {idx + 1}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {tabSwitches > 0 && (
            <span className={`as-tab-warn ${tabSwitches >= 4 ? "danger" : "warning"}`}>
              <AlertTriangle size={10} />
              {tabSwitches}/5
            </span>
          )}
          <span className={`as-timer ${timerDanger ? "danger" : timerWarning ? "warning" : ""}`}>
            <Clock size={11} />
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      {/* ── Split Container ── */}
      <div className="hr-split-container">
        {/* LEFT — Question */}
        <div className="hr-left-panel" style={{ width: `${leftWidth}%` }}>
          <div className="hr-panel-tabs">
            <span className="hr-panel-tab active"><Lightbulb size={11} /> Problem</span>
          </div>
          <div className="hr-question-body">
            <h5 className="hr-q-title">{currentQ?.title}</h5>
            <div className="hr-q-desc" dangerouslySetInnerHTML={{ __html: currentQ?.description }} />
            {testCases.length > 0 && (
              <div className="hr-tc-section">
                <div className="hr-tc-heading"><ListOrdered size={12} /> Sample Test Cases</div>
                {testCases.map((tc, i) => (
                  <div key={i} className="hr-tc-card">
                    <div className="hr-tc-row">
                      <span className="hr-tc-label">Input</span>
                      <pre className="hr-tc-pre">{tc.input || "(empty)"}</pre>
                    </div>
                    <div className="hr-tc-row">
                      <span className="hr-tc-label">Expected Output</span>
                      <pre className="hr-tc-pre">{tc.expected}</pre>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* DIVIDER */}
        <div className="hr-divider" onMouseDown={onDragStart}>
          <div className="hr-divider-grip" />
        </div>

        {/* RIGHT — Editor + I/O */}
        <div className="hr-right-panel" style={{ width: `${100 - leftWidth}%` }}>
          <div className="hr-editor-toolbar">
            <div className="hr-lang-select-wrap">
              <select
                value={currentLang}
                onChange={handleLangChange}
                className="hr-lang-select"
                disabled={isCurrentSubmitted}
                title={lockedLanguages.length ? `Languages are locked to: ${lockedLanguages.join(", ")}` : undefined}
              >
                {recommendedLangs.length > 0 && (
                  <optgroup label="⭐ Recommended">
                    {recommendedLangs.map(l => <option key={l.id} value={l.value}>{l.label}</option>)}
                  </optgroup>
                )}
                {otherLangs.length > 0 && (
                  <optgroup label={lockedLanguages.length ? "Languages" : "Other Languages"}>
                    {otherLangs.map(l => <option key={l.id} value={l.value}>{l.label}</option>)}
                  </optgroup>
                )}
              </select>
              <ChevronDown size="1em" className="hr-lang-chevron" />
            </div>
            <div className="hr-toolbar-actions">
              <button className="hr-action-btn run" onClick={handleRun} disabled={running || isCurrentSubmitted}>
                {running ? <Spinner size="sm" animation="border" /> : <><Play size={10} /> Run</>}
              </button>
              <button className="hr-action-btn test" onClick={handleRunAll} disabled={running || isCurrentSubmitted}>
                <Terminal size={10} /> Run Tests
              </button>
              <button className="hr-action-btn submit" onClick={handleSubmit} disabled={running || !results.length || isCurrentSubmitted}>
                <UploadCloud size={12} /> {isCurrentSubmitted ? "Submitted" : "Submit"}
              </button>
            </div>
          </div>

          <div className="hr-editor-area" style={{ flex: bottomOpen ? "1 1 60%" : "1 1 100%" }}>
            <Editor
              height="100%"
              language={languages.find(l => l.value === currentLang)?.monacoId || "javascript"}
              value={currentCode}
              onChange={(v) => handleCodeChange(v || "")}
              theme="vs-dark"
              onMount={onEditorMount}
              options={editorOptions(isCurrentSubmitted)}
            />
          </div>

          {/* Bottom I/O Panel */}
          <div className={`hr-bottom-panel ${bottomOpen ? "open" : "collapsed"}`}>
            <div className="hr-bottom-header" onClick={() => setBottomOpen(p => !p)}>
              <div className="hr-bottom-tabs">
                {TABS.map(t => (
                  <button
                    key={t.key}
                    className={`hr-bottom-tab ${activeTab === t.key ? "active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); setActiveTab(t.key); setBottomOpen(true); }}
                  >
                    {t.icon} {t.label}
                    {t.key === "results" && results.length > 0 && (
                      <span className={`hr-tab-badge ${passedCount === results.length ? "pass" : "mixed"}`}>
                        {passedCount}/{results.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <button className="hr-collapse-btn">
                {bottomOpen ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
              </button>
            </div>
            {bottomOpen && (
              <div className="hr-bottom-body">
                {activeTab === "input" && (
                  <textarea
                    className="hr-io-textarea"
                    placeholder="Enter custom input here…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    spellCheck={false}
                  />
                )}
                {activeTab === "output" && (
                  <pre className="hr-io-output">{output || "Run your code to see output"}</pre>
                )}
                {activeTab === "results" && (
                  <div className="hr-results-list">
                    {results.length === 0 ? (
                      <div className="hr-results-empty">
                        <Code size={20} />
                        <span>Run test cases to see results</span>
                      </div>
                    ) : (
                      <>
                        {visibleResults.map((r, i) => (
                          <div key={i} className={`hr-result-row ${r.passed ? "pass" : "fail"}`}>
                            <div className="hr-result-status">
                              {r.passed ? <CheckCircle2 size="1em" className="text-success" /> : <XCircle size="1em" className="text-danger" />}
                              <span>Case {i + 1}</span>
                            </div>
                            <div className="hr-result-data">
                              <div><span className="hr-rd-label">Input</span><code>{r.input || "(empty)"}</code></div>
                              <div><span className="hr-rd-label">Expected</span><code>{r.expected}</code></div>
                              <div><span className="hr-rd-label">Output</span><code>{r.output || "—"}</code></div>
                            </div>
                          </div>
                        ))}
                        {hiddenResults.length > 0 && (
                          <div className={`hr-result-row ${hiddenPassed === hiddenResults.length ? "pass" : "fail"} as-hidden-summary`}>
                            <div className="hr-result-status">
                              {hiddenPassed === hiddenResults.length
                                ? <CheckCircle2 size="1em" className="text-success" />
                                : <XCircle size="1em" className="text-danger" />}
                              <span>Hidden Test Cases</span>
                            </div>
                            <div className="as-hidden-detail">
                              {hiddenPassed} of {hiddenResults.length} hidden test case{hiddenResults.length !== 1 ? "s" : ""} passed
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default StudentAssignmentSessionPage;
